/**
 * Email Foundation v1 — Slice 2 — Forgot / Reset password pages.
 *
 * Bilingual DE/EN. Uses the existing Stockaily login design language
 * (dark card on soft neutral background, brand wordmark, restrained
 * typography).
 *
 * Security posture (see spec Section 5):
 *   * The reset token is delivered in the URL *fragment*
 *     (`#token=...`), so the browser NEVER sends it to the server,
 *     to analytics, or in the Referer header.
 *   * At the earliest safe point we (a) read the token from
 *     `window.location.hash`, (b) hold it in component memory,
 *     (c) sanitize the visible URL with `history.replaceState`.
 *   * We NEVER persist the token in `localStorage` or `sessionStorage`.
 *   * We NEVER include the token in any analytics event.
 *   * The token is submitted to the backend only in the request body
 *     over HTTPS.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useTranslation } from "@/lib/i18n";

/* ---------------------------- Copy ------------------------------- */
const COPY = {
  en: {
    forgot_title: "Forgot your password?",
    forgot_lead:
      "Enter the email you use for Stockaily and we'll send reset instructions.",
    forgot_email_label: "Email",
    forgot_submit: "Send reset instructions",
    forgot_success:
      "If an eligible account exists for this email address, we'll send password reset instructions.",
    forgot_back: "Back to sign in",
    reset_title: "Choose a new password",
    reset_lead: "Enter a new password for your Stockaily account.",
    reset_password_label: "New password",
    reset_password_confirm_label: "Confirm new password",
    reset_submit: "Reset password",
    reset_success: "Your password was reset. You can now sign in.",
    reset_error_invalid: "This reset link is invalid. Please request a new one.",
    reset_error_expired: "This reset link has expired. Please request a new one.",
    reset_error_used: "This reset link has already been used. Please request a new one.",
    reset_error_policy: "Please choose a longer password (at least 6 characters).",
    reset_error_mismatch: "The two passwords do not match.",
    reset_error_generic: "We couldn't reset your password. Please try again.",
    reset_no_token: "This reset link is missing its token. Please request a new one.",
    open_signin: "Go to sign in",
    request_new_link: "Request a new reset link",
  },
  de: {
    forgot_title: "Passwort vergessen?",
    forgot_lead:
      "Geben Sie die E-Mail-Adresse ein, die Sie bei Stockaily verwenden — wir senden Anweisungen zum Zurücksetzen.",
    forgot_email_label: "E-Mail",
    forgot_submit: "Anweisungen senden",
    forgot_success:
      "Wenn für diese E-Mail-Adresse ein berechtigtes Konto existiert, senden wir Anweisungen zum Zurücksetzen des Passworts.",
    forgot_back: "Zur Anmeldung",
    reset_title: "Neues Passwort wählen",
    reset_lead: "Geben Sie ein neues Passwort für Ihr Stockaily-Konto ein.",
    reset_password_label: "Neues Passwort",
    reset_password_confirm_label: "Neues Passwort bestätigen",
    reset_submit: "Passwort zurücksetzen",
    reset_success: "Ihr Passwort wurde zurückgesetzt. Sie können sich jetzt anmelden.",
    reset_error_invalid: "Dieser Link ist ungültig. Bitte fordern Sie einen neuen an.",
    reset_error_expired: "Dieser Link ist abgelaufen. Bitte fordern Sie einen neuen an.",
    reset_error_used: "Dieser Link wurde bereits verwendet. Bitte fordern Sie einen neuen an.",
    reset_error_policy: "Bitte wählen Sie ein längeres Passwort (mindestens 6 Zeichen).",
    reset_error_mismatch: "Die beiden Passwörter stimmen nicht überein.",
    reset_error_generic: "Passwort konnte nicht zurückgesetzt werden. Bitte erneut versuchen.",
    reset_no_token: "Diesem Link fehlt der Token. Bitte fordern Sie einen neuen an.",
    open_signin: "Zur Anmeldung",
    request_new_link: "Neuen Link anfordern",
  },
};

function useCopy() {
  const { lang } = useTranslation();
  return COPY[lang === "de" ? "de" : "en"];
}

/* ---------------------------- Shell ------------------------------ */
function AuthShell({ title, lead, children, "data-testid": testId }) {
  return (
    <div
      className="min-h-screen bg-[#0F1216] flex items-center justify-center p-4"
      data-testid={testId}
    >
      <div className="w-full max-w-[420px] bg-[#161B22] border border-white/10 rounded-2xl p-8 shadow-2xl">
        <div className="mb-6 text-center">
          <div className="text-white text-xl font-semibold tracking-tight">
            Stockaily
          </div>
        </div>
        <h1 className="text-white text-[22px] font-semibold leading-snug">
          {title}
        </h1>
        <p className="mt-2 text-white/60 text-sm leading-relaxed">{lead}</p>
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

const inputClass =
  "w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-white placeholder-white/30 focus:outline-none focus:border-[#3FAE89] focus:ring-1 focus:ring-[#3FAE89] transition-colors";
const primaryBtn =
  "w-full mt-4 rounded-lg bg-[#3FAE89] hover:bg-[#4bc39a] text-[#0F1216] font-semibold px-4 py-2.5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed";

/* ------------------------ ForgotPassword ------------------------- */
export function ForgotPassword() {
  const copy = useCopy();
  const { lang } = useTranslation();
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (pending) return;
    setPending(true);
    try {
      await api.post("/auth/forgot-password", {
        email: email.trim(),
        locale: lang === "de" ? "de" : "en",
      });
    } catch (_err) {
      // Even on network/server failure we surface the generic
      // success message so account existence is never revealed.
    }
    setDone(true);
    setPending(false);
  }

  return (
    <AuthShell
      title={copy.forgot_title}
      lead={copy.forgot_lead}
      data-testid="forgot-password-page"
    >
      {done ? (
        <div>
          <div
            className="rounded-lg bg-[#3FAE89]/10 border border-[#3FAE89]/30 text-[#4bc39a] text-sm p-3"
            data-testid="forgot-password-success"
          >
            {copy.forgot_success}
          </div>
          <Link
            to="/login"
            className="block text-center mt-4 text-sm text-white/70 hover:text-white transition-colors"
            data-testid="forgot-password-back-to-login"
          >
            {copy.forgot_back}
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit}>
          <label className="text-white/70 text-sm block mb-1.5">
            {copy.forgot_email_label}
          </label>
          <input
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
            data-testid="forgot-password-email-input"
          />
          <button
            type="submit"
            disabled={pending || !email}
            className={primaryBtn}
            data-testid="forgot-password-submit"
          >
            {copy.forgot_submit}
          </button>
          <Link
            to="/login"
            className="block text-center mt-3 text-sm text-white/60 hover:text-white transition-colors"
          >
            {copy.forgot_back}
          </Link>
        </form>
      )}
    </AuthShell>
  );
}

/* ------------------------- ResetPassword ------------------------- */
export function ResetPassword() {
  const copy = useCopy();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pending, setPending] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | ok | err
  const [errKey, setErrKey] = useState("");
  const tokenRef = useRef("");
  const [hasToken, setHasToken] = useState(false);

  // Step 1 — read the token from the URL FRAGMENT, then sanitize
  // the visible URL immediately.  Runs before we render anything
  // that could trigger a PostHog pageview capture with the raw
  // token, because our AnalyticsProvider allowlist explicitly
  // excludes password-reset routes (see analytics-allowlist.js).
  useEffect(() => {
    let extracted = "";
    try {
      const raw = window.location.hash || "";
      // `#token=...` — accept the token unchanged (URL-safe base64).
      const match = raw.match(/(?:^#|&)token=([^&]+)/);
      if (match && match[1]) {
        extracted = decodeURIComponent(match[1]);
      }
    } catch (_e) {
      extracted = "";
    }
    tokenRef.current = extracted;
    setHasToken(Boolean(extracted));
    // Sanitize the URL — remove the fragment so the token no longer
    // appears in the address bar, referrer or any subsequent
    // window.location read.  This must happen synchronously in the
    // first effect so analytics init cannot see the raw token.
    try {
      const cleanUrl = window.location.pathname + window.location.search;
      window.history.replaceState({}, "", cleanUrl);
    } catch (_e) {
      /* ignored — best effort */
    }
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    if (pending) return;
    setPending(true);
    setStatus("idle");
    setErrKey("");
    const token = tokenRef.current;
    if (!token) {
      setStatus("err");
      setErrKey("no_token");
      setPending(false);
      return;
    }
    if (password !== confirm) {
      setStatus("err");
      setErrKey("mismatch");
      setPending(false);
      return;
    }
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: password,
      });
      setStatus("ok");
      // Wipe the token from memory now that we no longer need it.
      tokenRef.current = "";
    } catch (err) {
      const detail =
        (err && err.response && err.response.data && err.response.data.detail) ||
        "";
      if (detail === "reset_token_expired") {
        setErrKey("expired");
      } else if (detail === "reset_token_used") {
        setErrKey("used");
      } else if (detail === "reset_token_invalid") {
        setErrKey("invalid");
      } else if (
        detail === "password_too_short" ||
        detail === "password_too_long" ||
        detail === "password_invalid"
      ) {
        setErrKey("policy");
      } else {
        setErrKey("generic");
      }
      setStatus("err");
    }
    setPending(false);
  }

  const errorMessage = (() => {
    switch (errKey) {
      case "invalid": return copy.reset_error_invalid;
      case "expired": return copy.reset_error_expired;
      case "used":    return copy.reset_error_used;
      case "policy":  return copy.reset_error_policy;
      case "mismatch":return copy.reset_error_mismatch;
      case "no_token":return copy.reset_no_token;
      default:        return copy.reset_error_generic;
    }
  })();

  // Terminal link states — recipient needs a fresh link.
  const isTerminalLinkError =
    errKey === "invalid" || errKey === "expired" ||
    errKey === "used" || errKey === "no_token";

  return (
    <AuthShell
      title={copy.reset_title}
      lead={copy.reset_lead}
      data-testid="reset-password-page"
    >
      {status === "ok" ? (
        <div>
          <div
            className="rounded-lg bg-[#3FAE89]/10 border border-[#3FAE89]/30 text-[#4bc39a] text-sm p-3"
            data-testid="reset-password-success"
          >
            {copy.reset_success}
          </div>
          <button
            onClick={() => navigate("/login")}
            className={primaryBtn}
            data-testid="reset-password-go-signin"
          >
            {copy.open_signin}
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit}>
          <label className="text-white/70 text-sm block mb-1.5" htmlFor="reset-password-input">
            {copy.reset_password_label}
          </label>
          <input
            id="reset-password-input"
            type="password"
            required
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
            minLength={6}
            autoComplete="new-password"
            data-testid="reset-password-input"
          />
          <label className="text-white/70 text-sm block mt-3 mb-1.5" htmlFor="reset-password-confirm-input">
            {copy.reset_password_confirm_label}
          </label>
          <input
            id="reset-password-confirm-input"
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={inputClass}
            minLength={6}
            autoComplete="new-password"
            data-testid="reset-password-confirm-input"
          />
          {status === "err" && (
            <div
              className="mt-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm p-3"
              data-testid="reset-password-error"
              data-error-key={errKey || "generic"}
              role="alert"
              aria-live="polite"
            >
              {errorMessage}
            </div>
          )}
          <button
            type="submit"
            disabled={pending || !password || !confirm || !hasToken || isTerminalLinkError}
            className={primaryBtn}
            data-testid="reset-password-submit"
          >
            {copy.reset_submit}
          </button>
          {isTerminalLinkError && (
            <Link
              to="/forgot-password"
              className="block text-center mt-3 text-sm text-white/70 hover:text-white transition-colors"
              data-testid="reset-password-request-new"
            >
              {copy.request_new_link}
            </Link>
          )}
        </form>
      )}
    </AuthShell>
  );
}

export default ForgotPassword;
